import { Component, createRef, h, Ref, RefObject, render } from "preact";
import { useState } from "preact/hooks";
import { decode, encode } from "msgpack-lite";

let application: App;

window.addEventListener("load", () => {
    render(<App ref={(v: App) => application = v}/>, document.getElementById("app")!);
});

let websocket: WebSocket | undefined;

function connectToStellaBIO(ip: string) {
    websocket = new WebSocket(`wss://${ip.replace(/\./mg, "-")}.wildcard.direct.egzotech.com:443`);
    websocket.binaryType = "arraybuffer";

    application.setState({
        connectionState: "connecting"
    });

    let hadError = false;
    let timeout: number | undefined;

    websocket.onclose = () => {
        if (hadError) {
            return;
        }

        application.setState({
            connectionState: "disconnected",
            sendState: "none"
        });

        if (timeout) {
            window.clearTimeout(timeout);
        }
    }

    websocket.onerror = () => {
        hadError = true;

        application.setState({
            connectionState: "error",
            sendState: "none"
        });

        if (timeout) {
            window.clearTimeout(timeout);
        }
    }

    websocket.onopen = () => {
        application.setState({
            connectionState: "connected"
        });

        timeout = window.setTimeout(() => {
            websocket?.close();
            application.setState({
                connectionState: "timeout",
                sendState: "none"
            });
        }, 10000);

        sendAdsMaskToStellaBIO();
        sendDacToStellaBIO(0, 2, 0, false);

        application.setState({ stellaValues: { muxMask: 0, ads: 2, outputCurrent: 0 }});
    }

    websocket.onmessage = (e) => {
        const data = decode(new Uint8Array(e.data));

        console.log(data);

        if (data.d) {
            const channels = [0, 0, 0, 0, 0, 0, 0, 0]

            for (const prop in data.d) {
                const arr = data.d[prop] as Int32Array;

                channels[parseInt(prop)] = (arr.reduce((prev, curr) => prev + curr, 0) / arr.length) * 2.4 / 0x7FFFFF / 24;
            }

            application.channelDataRef.current!.setState({ channelData: channels });
        }

        if (data.i) {
            application.channelDataRef.current!.setState({ impedanceData: Array.from(data.i as Int32Array) });
        }

        if (data.bat) {
            application.batteryDataRef.current!.setState({ bat: data.bat });
        }

        if (data.system) {
            application.batteryDataRef.current!.setState({ system: data.system });
        }

        if (data && data.bat) {
            if (timeout) {
                window.clearTimeout(timeout);
            }

            timeout = window.setTimeout(() => {
                websocket?.close();
                application.setState({
                    connectionState: "timeout",
                    sendState: "none"
                });
            }, 10000);
        }
    }
}

export function sendLedsToStellaBIO(arr: ArrayLike<number>) {
    if (!websocket) {
        return false;
    }

    if (arr.length !== 12) {
        return false;
    }

    const copy = Array.from(arr);

    for (let i = 0; i < copy.length; i++) {
        let r = (arr[i] >> 16) & 0xff;
        let g = (arr[i] >> 8) & 0xff;
        let b = (arr[i]) & 0xff;

        r = Math.floor(r / 4);
        g = Math.floor(g / 4);
        b = Math.floor(b / 4);

        copy[i] = (r << 16) | (g << 8) | b;
    }

    const a = encode({ led: new Uint32Array(copy) });
    websocket.send(a);
    return true;
}

export function sendDacToStellaBIO(mux: number, ads: number, outputCurrent: number, enabled: boolean) {
    if (!websocket) {
        return "Not connected";
    }

    if (mux < 0 || mux > 0xFFFFFFFF) {
        return "MUX Mask invalid";
    }

    if (ads < 1 || ads > 2) {
        return "ADS value invalid";
    }

    if (outputCurrent < -140 || outputCurrent > 140) {
        return "Output current invalid";
    }

    try {
        const encoded = encode({
            service: {
                outputdac: {
                    mux: mux | 0,
                    ads,
                    raw: outputCurrent,
                    ena: enabled
                }
            }
        });

        console.log(encoded);

        websocket.send(encoded);
    }
    catch(ex) {
        return "Communication error";
    }

    return true;
}

function sendAdsMaskToStellaBIO() {
    if (!websocket) {
        return "Not connected";
    }

    const encoded = encode({
        ads: {
            mask: 0xff
        }
    });

    websocket.send(encoded);

    return true;
}

function dacMuxOptionsToNumber() {
    const mux = JSON.parse(JSON.stringify(dacMuxOptions)) as { value: boolean; bit: number }[];
    mux.sort((a, b) => a.bit - b.bit);

    console.log(mux);

    const bitData = [] as boolean[];

    for (let i = 0; i < mux.length; i++) {
        bitData[mux[i].bit] = mux[i].value;
    }

    let output = 0;

    for (let i = 0; i < bitData.length; i++) {
        output |= bitData[i] ? 1 << i : 0;
    }

    if (output < 0) {
        output = 0xFFFFFFFF + (output + 1);
    }

    return output;
}

const dacMuxOptions = [
    {
        name: "MUX_EMSP",
        bit: 0,
        value: false,
    },
    {
        name: "MUX_EMSN",
        bit: 1,
        value: false,
    },
    {
        name: "MUX_AIN1P",
        bit: 13,
        value: false,
    },
    {
        name: "MUX_AIN1N",
        bit: 12,
        value: false,
    },
    {
        name: "MUX_AIN2P",
        bit: 16,
        value: false,
    },
    {
        name: "MUX_AIN2N",
        bit: 17,
        value: false,
    },
    {
        name: "MUX_AIN3P",
        bit: 14,
        value: false,
    },
    {
        name: "MUX_AIN3N",
        bit: 15,
        value: false,
    },
    {
        name: "MUX_AIN4P",
        bit: 10,
        value: false,
    },
    {
        name: "MUX_AIN4N",
        bit: 11,
        value: false,
    },
    {
        name: "MUX_AIN5P",
        bit: 9,
        value: false,
    },
    {
        name: "MUX_AIN5N",
        bit: 8,
        value: false,
    },
    {
        name: "MUX_AIN6P",
        bit: 3,
        value: false,
    },
    {
        name: "MUX_AIN6N",
        bit: 2,
        value: false,
    },
    {
        name: "MUX_AIN7P",
        bit: 5,
        value: false,
    },
    {
        name: "MUX_AIN7N",
        bit: 4,
        value: false,
    },
    {
        name: "MUX_AIN8P",
        bit: 7,
        value: false,
    },
    {
        name: "MUX_AIN8N",
        bit: 6,
        value: false,
    },
    {
        name: "MUX_COM",
        bit: 31,
        value: false,
    },
    {
        name: "MUX_COM_100R",
        bit: 28,
        value: false,
    },
    {
        name: "MUX_COM_500R",
        bit: 30,
        value: false,
    },
    {
        name: "MUX_COM_1k",
        bit: 29,
        value: false,
    },
    {
        name: "MUX_COM_10k",
        bit: 27,
        value: false,
    },
    {
        name: "MUX_COM_47k",
        bit: 26,
        value: false,
    },
    {
        name: "MUX_COM_100k",
        bit: 25,
        value: false,
    },
    {
        name: "MUX_COM_1M",
        bit: 24,
        value: false,
    },
    {
        name: "MUX_COM_100n",
        bit: 23,
        value: false,
    },
    {
        name: "MUX_COM_10n",
        bit: 22,
        value: false,
    },
    {
        name: "MUX_POS_1M",
        bit: 18,
        value: false,
    },
    {
        name: "MUX_NEG_1M",
        bit: 20,
        value: false,
    },
    {
        name: "MUX_POS_240R",
        bit: 19,
        value: false,
    },
    {
        name: "MUX_NEG_240R",
        bit: 21,
        value: false,
    },
]


class App extends Component {
    state = {
        ip: "192.168.1.146",
        connectionState: "none",
        channelData: [0, 0, 0, 0, 0, 0, 0, 0],
        ads: 2 as 1 | 2,
        outputCurrent: "0",
        dacEnabled: false,
        sendState: "none",
        stellaValues: { muxMask: 0, ads: 2, outputCurrent: 0, dacEnabled: false }
    }

    channelDataRef: RefObject<ChannelData> = createRef();
    batteryDataRef: RefObject<BatteryAndSystemData> = createRef();

    render() {

        // = new WebSocket(`wss://${(parameters.ip ?? "192.168.1.221").replace(/\./mg, "-")}.wildcard.direct.egzotech.com:${parameters.port ?? "8443"}`);
        // this.websocket.binaryType = "arraybuffer";
        return <div style="max-width: 1200px; margin: auto; margin-top: 20px;">
            <div>
                <div class="paper" style="padding: 10px; display: flex; align-items: center;">
                    <span>StellaBIO ip address:</span>
                    <input type="text" value={this.state.ip} style="margin-left: 10px" onChange={(e) => this.setState({ ip: e.currentTarget.value})}></input>
                    <button type="button" style="margin-left: 10px" onClick={() => connectToStellaBIO(this.state.ip)}>Connect</button>
                    <button type="button" class={"error" + (this.state.connectionState === "connected" ? "" : " disabled")} style="margin-left: 10px" onClick={() => websocket?.close()}>Disconnect</button>
                    {this.state.connectionState === "none" ? null :
                        this.state.connectionState === "connected" ?
                        <span class="alert success" style="margin-left: 10px">Connected</span> :
                        this.state.connectionState === "error" ?
                        <span class="alert error" style="margin-left: 10px">Error occured</span> :
                        this.state.connectionState === "connecting" ?
                        <span class="alert info" style="margin-left: 10px">Connecting...</span> :
                        this.state.connectionState === "disconnected" ?
                        <span class="alert warning" style="margin-left: 10px">Disconnected</span> :
                        this.state.connectionState === "timeout" ?
                        <span class="alert error" style="margin-left: 10px">Timed out</span> :
                        null
                     }
                </div>
                {this.state.connectionState === "connected" || true ?
                    <div style="display: flex; margin-top: 10px;">
                        <div class="paper" style="padding: 10px">
                            <div style="font-weight: bold; font-size: 16px; padding-bottom: 10px">MUX Options</div>
                            <div style="margin: 10px 0px">
                                <button onClick={() => {
                                    for (let i = 0; i < dacMuxOptions.length; i++) {
                                        dacMuxOptions[i].value = false;
                                        this.forceUpdate();
                                    }
                                }}>All Off</button>
                            </div>
                            <div style="display: grid; grid-template-columns: auto auto;">
                                <div class="list" style="display: grid; grid-template-columns: auto auto; align-items: center; align-self: flex-start;">
                                    {dacMuxOptions.slice(2, 18).map((option) => [
                                        <div class={"item " + (option.value ? " selected" : "")} style="--primary-color: #5279a5; padding: 2px" onClick={(e) => {
                                            option.value = !option.value;
                                            this.forceUpdate();
                                        }}>{option.name}</div>,
                                        <input style="margin-left: 10px" type="checkbox" checked={option.value} onChange={(e) => {
                                            option.value = e.currentTarget.checked;
                                            this.forceUpdate();
                                        }}/>
                                    ])}
                                </div>
                                <div class="list" style="display: grid; grid-template-columns: auto auto; align-items: center; align-self: flex-start;">
                                    {dacMuxOptions.slice(18).map((option) => [
                                        <div class={"item " + (option.value ? " selected" : "")} style="--primary-color: #5279a5; padding: 2px" onClick={(e) => {
                                            option.value = !option.value;
                                            this.forceUpdate();
                                        }}>{option.name}</div>,
                                        <input style="margin-left: 10px" type="checkbox" checked={option.value} onChange={(e) => {
                                            option.value = e.currentTarget.checked;
                                            this.forceUpdate();
                                        }}/>
                                    ])}
                                    <div style="margin-top: 50px;"/><div style="margin-top: 50px;"/>
                                    {dacMuxOptions.slice(0, 2).map((option) => [
                                        <div class={"item " + (option.value ? " selected" : "")} style="--primary-color: #5279a5; padding: 2px" onClick={(e) => {
                                            option.value = !option.value;
                                            this.forceUpdate();
                                        }}>{option.name}</div>,
                                        <input style="margin-left: 10px" type="checkbox" checked={option.value} onChange={(e) => {
                                            option.value = e.currentTarget.checked;
                                            this.forceUpdate();
                                        }}/>
                                    ])}
                                </div>
                            </div>
                        </div>
                        <div class="paper" style="margin-left: 10px; padding: 10px">
                            <div style="font-weight: bold; font-size: 16px; padding-bottom: 10px">MUX Channel</div>
                            <div style="margin: 10px 0px">
                                <button onClick={() => {
                                    for (let i = 2; i < dacMuxOptions.length && i < 18; i++) {
                                        dacMuxOptions[i].value = true;
                                        this.forceUpdate();
                                    }
                                }}>All On</button>
                            </div>
                            <div style="display: grid; grid-template-columns: auto auto;">
                                <div class="list" style="display: grid; grid-template-columns: auto auto; align-items: center; align-self: flex-start;">
                                    {(() => {
                                        const pairs: [
                                            { name: string, bit: number, value: boolean },
                                            { name: string, bit: number, value: boolean }
                                        ][] = [];
                                        const options = dacMuxOptions.slice(2, 18);

                                        let moreThanOneSelected = false;
                                        let onlyOneSelected = false;

                                        for (let i = 0; i < options.length; i += 2) {
                                            pairs.push([options[i], options[i + 1]]);

                                            if (options[i].value && options[i + 1].value && !onlyOneSelected) {
                                                onlyOneSelected = true;
                                                continue;
                                            }

                                            if (options[i].value && options[i + 1].value && onlyOneSelected) {
                                                moreThanOneSelected = true;
                                                continue;
                                            }
                                        }

                                        return pairs.map(([p1, p2]) => [
                                            <div class={"item " + (p1.value && p2.value ? " selected" : "")} style="--primary-color: #5279a5; padding: 2px" onClick={() => {
                                                for (let i = 0; i < pairs.length; i++) {
                                                    pairs[i][0].value = false;
                                                    pairs[i][1].value = false;
                                                }

                                                p1.value = true;
                                                p2.value = true;

                                                this.forceUpdate();
                                            }}>
                                                {p1.name.substr(0, p1.name.length - 1)}
                                            </div>,
                                            <input name="mux-ain" style="margin-left: 10px" type="radio" checked={p1.value && p2.value && !moreThanOneSelected} onChange={(e) => {
                                                for (let i = 0; i < pairs.length; i++) {
                                                    pairs[i][0].value = false;
                                                    pairs[i][1].value = false;
                                                }

                                                p1.value = e.currentTarget.checked;
                                                p2.value = e.currentTarget.checked;

                                                this.forceUpdate();
                                            }}/>
                                    ])})()}
                                </div>
                            </div>
                        </div>
                        <div style="display: flex; flex-direction: column; flex: 1 1 0;">
                            <div style="display: flex;">
                                <div class="paper" style="margin-left: 10px; padding: 10px; flex: 1 1 0;">
                                    <div style="font-weight: bold; font-size: 16px; padding-bottom: 20px">Send DAC options</div>
                                    <div>
                                        <div>
                                            <div style="font-family: monospace">MUX Mask:</div>
                                            <div>{dacMuxOptionsToNumber().toString(2)}</div>
                                            <div style="font-family: monospace">(0x{dacMuxOptionsToNumber().toString(16)})</div>
                                            <div style="margin-top: 10px">ADS: {this.state.ads}</div>
                                            <div>Output Current: {this.state.outputCurrent}</div>
                                            <div>DAC Power: {this.state.dacEnabled ? "enabled" : "disabled"}</div>
                                        </div>
                                        <div style="display: flex; align-items: center; margin-top: 10px">
                                            <button class={this.state.connectionState === "connected" ? "" : "disabled"} onClick={() => {
                                                const muxMask = dacMuxOptionsToNumber();
                                                const error = sendDacToStellaBIO(dacMuxOptionsToNumber(), this.state.ads, parseInt(this.state.outputCurrent), this.state.dacEnabled);

                                                if (error === true) {
                                                    this.setState({ sendState: "ok", stellaValues: { muxMask, ads: this.state.ads, outputCurrent: this.state.outputCurrent, dacEnabled: this.state.dacEnabled } });
                                                    setTimeout(() => this.setState({ sendState: "none" }), 1000);
                                                }
                                                else {
                                                    this.setState({ sendState: error });
                                                }
                                            }}>Send</button>

                                            <div style="margin-left: 10px">
                                                {this.state.sendState === "ok" ? <div class="alert success">
                                                    Sending completed
                                                </div> : this.state.sendState === "none" ? null : <div class="alert error">
                                                    Error: {this.state.sendState}
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="paper" style="margin-left: 10px; padding: 10px;">
                                    <div style="font-weight: bold; font-size: 16px; padding-bottom: 20px">ADS Pass/Block</div>
                                    <div>
                                        <label>Pass<input type="radio" name="ads" value="1" checked={this.state.ads === 1} onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                this.setState({ ads: 1 });
                                            }
                                        }}></input></label>
                                    </div>
                                    <div>
                                        <label>Block<input type="radio" name="ads" value="2" checked={this.state.ads === 2} onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                this.setState({ ads: 2 });
                                            }
                                        }}></input></label>
                                    </div>
                                </div>
                                <div class="paper" style="margin-left: 10px; padding: 10px;">
                                    <div style="font-weight: bold; font-size: 16px; padding-bottom: 20px">Output current (RAW)</div>
                                    <div>
                                        <input type="number" min="-140" max="140" value={this.state.outputCurrent} onChange={(e) => {
                                            let v = parseInt(e.currentTarget.value);

                                            if (isNaN(v)) {
                                                this.setState({ outputCurrent: e.currentTarget.value });
                                                return;
                                            }

                                            v = v > 140 ? 140 : (v < -140 ? -140 : v);

                                            this.setState({ outputCurrent: v.toString() });
                                        }}></input>
                                    </div>
                                </div>
                                <div class="paper" style="margin-left: 10px; padding: 10px;">
                                    <div style="font-weight: bold; font-size: 16px; padding-bottom: 20px">Enable DAC power</div>
                                    <div>
                                        <label>Disable<input type="radio" name="dacEnable" value="1" checked={this.state.dacEnabled === false} onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                this.setState({ dacEnabled: false });
                                            }
                                        }}></input></label>
                                    </div>
                                    <div>
                                        <label>Enable<input type="radio" name="dacEnable" value="2" checked={this.state.dacEnabled === true} onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                this.setState({ dacEnabled: true });
                                            }
                                        }}></input></label>
                                    </div>
                                </div>
                            </div>
                            <div style="display: flex; margin-top: 10px;">
                                {this.state.connectionState === "connected" ? [
                                <div class="paper" style="margin-left: 10px; padding: 10px; min-width: 200px; flex: 1 1 0;">
                                    <div style="font-weight: bold; font-size: 16px; padding-bottom: 20px;">Current Stella values</div>
                                    <div>
                                        <div style="font-family: monospace">MUX Mask:</div>
                                        <div>{dacMuxOptionsToNumber().toString(2)}</div>
                                        <div style="font-family: monospace">(0x{dacMuxOptionsToNumber().toString(16)})</div>
                                        <div style="margin-top: 10px">ADS: {this.state.ads}</div>
                                        <div>Output Current: {this.state.stellaValues.outputCurrent}</div>
                                        <div>DAC Power: {this.state.stellaValues.dacEnabled ? "enabled" : "disabled"}</div>
                                    </div>
                                </div>,
                                <div class="paper" style="margin-left: 10px; padding: 10px; min-width: 200px">
                                    <div style="font-weight: bold; font-size: 16px; padding-bottom: 10px">Battery and system</div>

                                    <BatteryAndSystemData ref={this.batteryDataRef}/>
                                </div>] : null}
                            </div>
                            <div style="display: flex; margin-top: 10px;">
                                {this.state.connectionState === "connected" ? [
                                <div class="paper" style="margin-left: 10px; padding: 10px; min-width: 200px">
                                    <div style="font-weight: bold; font-size: 16px; padding-bottom: 10px">Channel outputs</div>
                                    {this.state.stellaValues.ads === 2 ?
                                        <div><div class="alert warning">ADS is blocked</div></div> :
                                        null}

                                    <ChannelData ref={this.channelDataRef}/>
                                </div>] : null}
                            </div>
                        </div>
                    </div>
                : null}
            </div>
        </div>;
    }
}

class ChannelData extends Component {
    state = {
        channelData: [0, 0, 0, 0, 0, 0, 0, 0],
        impedanceData: [0, 0, 0, 0, 0, 0, 0, 0]
    }

    render() {
        return <div style="margin-top: 10px; display: grid; grid-template-columns: auto auto auto; min-width: 300px">
            <div style="margin-top: 2px; font-weight: bold">Channel</div>
            <div style="margin-top: 2px; font-weight: bold">Output</div>
            <div style="margin-top: 2px; font-weight: bold">Impedance</div>

            {this.state.channelData.map((c, i) => [
                <div style="margin-top: 2px;">{i + 1}</div>,
                <span style="font-family: monospace;">{c.toFixed(6)} V</span>,
                <span style="font-family: monospace;">{this.state.impedanceData[i].toFixed(0)}</span>
            ])}
        </div>;
    }
}

class BatteryAndSystemData extends Component {
    state = {
        bat: {vol: 0, cur: 0, pow: 0, tem: 0},
        system: {mem: 0}
    }

    render() {
        return <div style="margin-top: 10px; display: grid; grid-template-columns: auto auto;">
            <div>Voltage:</div><div>{this.state.bat.vol.toFixed(3)} V</div>
            <div>Current:</div><div>{this.state.bat.cur.toFixed(3)} A</div>
            <div>Power:</div><div>{this.state.bat.pow.toFixed(3)} W</div>
            <div>Temperature:</div><div>{this.state.bat.tem.toFixed(1)} °C</div>
            <div style="margin-top: 10px">RAM:</div><div style="margin-top: 10px">{this.state.system.mem.toFixed(0)} B</div>
        </div>;
    }
}